// src/components/learning/ExerciseGame.js
// Composant principal pour les exercices interactifs

import React, { useState, useEffect, useCallback } from 'react';
import Confetti, { StarBurst, Trophy } from './Confetti';
import { encouragements, scoreThresholds } from './exercisesData';

const ExerciseGame = ({ exercise, onClose, onComplete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showStar, setShowStar] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiType, setConfettiType] = useState('confetti');
  const [showTrophy, setShowTrophy] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  
  // Timer pour quiz chrono
  const [timeLeft, setTimeLeft] = useState(exercise.timeLimit || null);
  const [timerActive, setTimerActive] = useState(false);

  const questions = exercise.questions;
  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;
  const progress = ((currentIndex) / totalQuestions) * 100;

  // Jouer le son de la lettre/mot
  const playSound = useCallback((text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'ar-SA';
      utterance.rate = 0.8;
      window.speechSynthesis.speak(utterance);
    }
  }, []);

  // Jouer le son automatiquement pour les exercices d'écoute
  useEffect(() => {
    if (exercise.type === 'listen-choose' && currentQuestion) {
      const timer = setTimeout(() => {
        playSound(currentQuestion.correct);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, exercise.type, currentQuestion, playSound]);

  // Timer pour quiz chrono
  useEffect(() => {
    if (exercise.type === 'speed-quiz' && !timerActive && !showResult) {
      setTimerActive(true);
    }
    
    if (timerActive && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setShowResult(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timerActive, timeLeft, exercise.type, showResult]);

  // Gérer la sélection d'une réponse
  const handleAnswer = (option) => {
    if (selectedOption !== null) return; // Déjà répondu
    
    setSelectedOption(option);
    const correct = option === currentQuestion.correct;
    setIsCorrect(correct);
    
    if (correct) {
      // Bonne réponse
      const points = attempts === 0 ? 10 : 5;
      setScore(prev => prev + points);
      setAnswers(prev => [...prev, { ...currentQuestion, userAnswer: option, correct: true, attempts: attempts + 1 }]);
      
      // Feedback positif
      const randomEncouragement = encouragements.correct[Math.floor(Math.random() * encouragements.correct.length)];
      setFeedback({ type: 'correct', message: randomEncouragement });
      setShowStar(true);
      
      // Passer à la question suivante après un délai
      setTimeout(() => {
        setShowStar(false);
        setFeedback(null);
        setSelectedOption(null);
        setIsCorrect(null);
        setAttempts(0);
        
        if (currentIndex < totalQuestions - 1) {
          setCurrentIndex(prev => prev + 1);
        } else {
          // Fin de l'exercice
          finishExercise();
        }
      }, 1200);
      
    } else {
      // Mauvaise réponse
      setAttempts(prev => prev + 1);
      const randomEncouragement = encouragements.wrong[Math.floor(Math.random() * encouragements.wrong.length)];
      setFeedback({ type: 'wrong', message: randomEncouragement });
      
      // Permettre de réessayer après un court délai
      setTimeout(() => {
        setSelectedOption(null);
        setIsCorrect(null);
        setFeedback(null);
      }, 1000);
    }
  };

  // Terminer l'exercice
  const finishExercise = () => {
    const percentage = Math.round((score / (totalQuestions * 10)) * 100);
    
    if (percentage >= scoreThresholds.perfect) {
      setShowTrophy(true);
      setConfettiType('gold');
      setTimeout(() => setShowConfetti(true), 500);
    } else if (percentage >= scoreThresholds.excellent) {
      setConfettiType('gold');
      setShowConfetti(true);
    } else if (percentage >= scoreThresholds.good) {
      setConfettiType('confetti');
      setShowConfetti(true);
    }
    
    setTimeout(() => {
      setShowResult(true);
    }, percentage >= scoreThresholds.perfect ? 2000 : 500);
  };

  // Rejouer l'audio
  const replayAudio = () => {
    if (currentQuestion) {
      playSound(currentQuestion.correct);
    }
  };

  // Obtenir le message de résultat
  const getResultMessage = () => {
    const percentage = Math.round((score / (totalQuestions * 10)) * 100);
    
    if (percentage >= 100) return { emoji: '🏆', title: 'PARFAIT !', subtitle: 'Tu es un champion !' };
    if (percentage >= 90) return { emoji: '🌟', title: 'EXCELLENT !', subtitle: 'Presque parfait !' };
    if (percentage >= 70) return { emoji: '🎉', title: 'BRAVO !', subtitle: 'Très bien joué !' };
    if (percentage >= 50) return { emoji: '👍', title: 'PAS MAL !', subtitle: 'Continue comme ça !' };
    return { emoji: '💪', title: 'COURAGE !', subtitle: 'Réessaie, tu vas y arriver !' };
  };

  // Écran de résultat
  if (showResult) {
    const percentage = Math.round((score / (totalQuestions * 10)) * 100);
    const result = getResultMessage();
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        {showConfetti && <Confetti type={confettiType} duration={4000} />}
        
        <div style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          borderRadius: '24px',
          padding: '3rem',
          textAlign: 'center',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
        }}>
          <div style={{ fontSize: '6rem', marginBottom: '1rem' }}>
            {result.emoji}
          </div>
          
          <h2 style={{ 
            color: 'white', 
            fontSize: '2.5rem', 
            margin: '0 0 0.5rem 0',
            textShadow: '0 2px 10px rgba(0,0,0,0.3)'
          }}>
            {result.title}
          </h2>
          
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1.2rem', margin: '0 0 2rem 0' }}>
            {result.subtitle}
          </p>
          
          {/* Score */}
          <div style={{
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '16px',
            padding: '1.5rem',
            marginBottom: '2rem'
          }}>
            <div style={{ 
              fontSize: '3rem', 
              fontWeight: 'bold', 
              color: percentage >= 70 ? '#10b981' : '#f59e0b',
              marginBottom: '0.5rem'
            }}>
              {percentage}%
            </div>
            <div style={{ color: 'rgba(255,255,255,0.7)' }}>
              {score} points sur {totalQuestions * 10}
            </div>
            <div style={{ 
              marginTop: '1rem',
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem',
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.9rem'
            }}>
              <span>✅ {answers.filter(a => a.correct).length} bonnes</span>
              <span>❌ {answers.filter(a => !a.correct).length} erreurs</span>
            </div>
          </div>
          
          {/* Boutons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              onClick={() => {
                setCurrentIndex(0);
                setScore(0);
                setAnswers([]);
                setShowResult(false);
                setShowConfetti(false);
                setShowTrophy(false);
                setAttempts(0);
                if (exercise.timeLimit) setTimeLeft(exercise.timeLimit);
              }}
              style={{
                padding: '1rem 2rem',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              🔄 Rejouer
            </button>
            
            <button
              onClick={onClose}
              style={{
                padding: '1rem 2rem',
                borderRadius: '12px',
                border: '2px solid rgba(255,255,255,0.3)',
                background: 'transparent',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              ✕ Fermer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 9999
    }}>
      {showStar && <StarBurst />}
      {showTrophy && <Trophy />}
      
      {/* Header */}
      <div style={{
        padding: '1rem 1.5rem',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '2rem' }}>{exercise.icon}</span>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.3rem' }}>
              {exercise.name}
            </h2>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
              {exercise.description}
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          {/* Timer pour quiz chrono */}
          {exercise.type === 'speed-quiz' && (
            <div style={{
              background: timeLeft <= 10 ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255,255,255,0.1)',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              color: timeLeft <= 10 ? '#ef4444' : 'white',
              fontWeight: 'bold',
              fontSize: '1.2rem'
            }}>
              ⏱️ {timeLeft}s
            </div>
          )}
          
          {/* Score */}
          <div style={{
            background: 'rgba(16, 185, 129, 0.2)',
            padding: '0.5rem 1rem',
            borderRadius: '8px',
            color: '#10b981',
            fontWeight: 'bold'
          }}>
            ⭐ {score} pts
          </div>
          
          {/* Question counter */}
          <div style={{ color: 'white' }}>
            {currentIndex + 1} / {totalQuestions}
          </div>
          
          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '8px',
              padding: '0.5rem 1rem',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1.2rem'
            }}
          >
            ✕
          </button>
        </div>
      </div>
      
      {/* Progress bar */}
      <div style={{
        height: '4px',
        background: 'rgba(255,255,255,0.1)'
      }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #667eea, #764ba2)',
          transition: 'width 0.3s ease'
        }} />
      </div>
      
      {/* Main content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        gap: '2rem'
      }}>
        {/* Question */}
        <div style={{ textAlign: 'center' }}>
          {exercise.type === 'listen-choose' && (
            <>
              <div 
                onClick={replayAudio}
                style={{
                  fontSize: '8rem',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  marginBottom: '1rem'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              >
                🔊
              </div>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.1rem' }}>
                Clique pour réécouter
              </p>
            </>
          )}
          
          {exercise.type === 'find-letter' && (
            <div style={{
              fontSize: '10rem',
              fontFamily: "'Amiri', 'Traditional Arabic', serif",
              color: 'white',
              textShadow: '0 4px 20px rgba(102, 126, 234, 0.5)'
            }}>
              {currentQuestion.letter}
            </div>
          )}
          
          {exercise.type === 'translate' && (
            <div style={{
              fontSize: '3rem',
              color: 'white',
              padding: '1rem 2rem',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '16px'
            }}>
              🇫🇷 {currentQuestion.french}
            </div>
          )}
          
          {exercise.type === 'complete-word' && (
            <div style={{
              fontSize: '4rem',
              fontFamily: "'Amiri', 'Traditional Arabic', serif",
              color: 'white',
              direction: 'rtl'
            }}>
              {currentQuestion.word}
            </div>
          )}
          
          {exercise.type === 'speed-quiz' && (
            <div style={{
              fontSize: '10rem',
              fontFamily: "'Amiri', 'Traditional Arabic', serif",
              color: 'white',
              textShadow: '0 4px 20px rgba(102, 126, 234, 0.5)'
            }}>
              {currentQuestion.letter}
            </div>
          )}
        </div>
        
        {/* Feedback */}
        {feedback && (
          <div style={{
            padding: '1rem 2rem',
            borderRadius: '12px',
            background: feedback.type === 'correct' 
              ? 'rgba(16, 185, 129, 0.2)' 
              : 'rgba(239, 68, 68, 0.2)',
            color: feedback.type === 'correct' ? '#10b981' : '#ef4444',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            animation: 'fadeIn 0.3s ease'
          }}>
            {feedback.message}
          </div>
        )}
        
        {/* Options */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '1rem',
          maxWidth: '600px',
          width: '100%'
        }}>
          {currentQuestion.options.map((option, index) => {
            const isSelected = selectedOption === option;
            const isCorrectOption = option === currentQuestion.correct;
            
            let backgroundColor = 'rgba(255,255,255,0.1)';
            let borderColor = 'rgba(255,255,255,0.2)';
            
            if (isSelected) {
              if (isCorrect) {
                backgroundColor = 'rgba(16, 185, 129, 0.3)';
                borderColor = '#10b981';
              } else {
                backgroundColor = 'rgba(239, 68, 68, 0.3)';
                borderColor = '#ef4444';
              }
            }
            
            // Montrer la bonne réponse après une erreur
            if (isCorrect === false && isCorrectOption) {
              backgroundColor = 'rgba(16, 185, 129, 0.2)';
              borderColor = '#10b981';
            }
            
            return (
              <button
                key={index}
                onClick={() => handleAnswer(option)}
                disabled={selectedOption !== null}
                style={{
                  padding: '1.5rem',
                  borderRadius: '16px',
                  border: `3px solid ${borderColor}`,
                  background: backgroundColor,
                  color: 'white',
                  fontSize: exercise.type === 'listen-choose' || exercise.type === 'translate' || exercise.type === 'complete-word'
                    ? '3rem' 
                    : '1.5rem',
                  fontFamily: exercise.type === 'listen-choose' || exercise.type === 'translate' || exercise.type === 'complete-word'
                    ? "'Amiri', 'Traditional Arabic', serif"
                    : 'inherit',
                  cursor: selectedOption !== null ? 'default' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: selectedOption !== null && !isSelected && !isCorrectOption ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (selectedOption === null) {
                    e.target.style.transform = 'scale(1.05)';
                    e.target.style.borderColor = '#667eea';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedOption === null) {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.borderColor = 'rgba(255,255,255,0.2)';
                  }
                }}
              >
                {option}
              </button>
            );
          })}
        </div>
        
        {/* Hint */}
        {attempts >= 2 && currentQuestion.hint && (
          <div style={{
            padding: '1rem',
            background: 'rgba(245, 158, 11, 0.2)',
            borderRadius: '8px',
            color: '#f59e0b',
            fontSize: '1rem'
          }}>
            💡 {currentQuestion.hint}
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ExerciseGame;
